import { Component, ErrorHandler, Inject, ChangeDetectionStrategy, signal } from '@angular/core'
import { form, maxLength, pattern, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Maksukortti } from '@lemon/core-firebase/model/maksukortti'
import { WebMaksukorttiLisaaPyynto, WebMaksukorttiLisaaVastaus } from '@lemon/core-firebase/model/web-maksukortti-api'
import { FirebaseLemonaid } from '../../_angular/service/firebase-lemonaid.service'
import { asetaArvo } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'
import { muotoileKortinNumero, onkoKorttiJoTallennettu } from './maksukortti-logiikka'

export interface MaksukorttiLisaaDialogData {
  olemassaolevat: Maksukortti[]
}

interface MaksukorttiLomake {
  maksutapa: number | null
  viimeiset4: string
  nimi: string
}

@Component({
  templateUrl: './maksukortti-lisaa.dialog.html',
  styleUrls: ['./maksukortti-lisaa.dialog.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MaksukorttiLisaaDialog {
  lomake = form(signal<MaksukorttiLomake>({ maksutapa: null, viimeiset4: '', nimi: '' }), polku => {
    required(polku.maksutapa)
    required(polku.viimeiset4)
    pattern(polku.viimeiset4, /^\d{4}$/)
    maxLength(polku.viimeiset4, 4)
    maxLength(polku.nimi, 200)
  })
  readonly virhe = signal('')
  readonly tallennetaan = signal(false)

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: MaksukorttiLisaaDialogData,
    private _dialogRef: MatDialogRef<MaksukorttiLisaaDialog, boolean>,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _translationService: LemonTranslationService,
    private _errorHandler: ErrorHandler,
  ) {}

  valitseMaksutapa(tunniste: number) {
    asetaArvo(this.lomake.maksutapa, tunniste)
    this.lomake.maksutapa().markAsTouched()
  }

  siivoaNumero() {
    const kentta = this.lomake.viimeiset4
    const siivottu = kentta().value().replace(/\D/g, '').slice(0, 4)
    if (siivottu !== kentta().value()) {
      asetaArvo(kentta, siivottu)
    }
    this.virhe.set('')
  }

  async tallenna() {
    if (this.lomake().invalid() || this.tallennetaan()) {
      return
    }
    const arvot = this.lomake().value()
    if (onkoKorttiJoTallennettu(this._data.olemassaolevat, arvot.viimeiset4)) {
      this.virhe.set(this._translationService.lokalisoi('tositteet.maksukortit.lisaa-dialog.duplikaatti', { numero: muotoileKortinNumero(arvot.viimeiset4) }))
      return
    }

    this.tallennetaan.set(true)
    this.virhe.set('')
    try {
      const pyynto: WebMaksukorttiLisaaPyynto = { maksutapaTunniste: arvot.maksutapa, viimeiset4: arvot.viimeiset4 }
      const nimi = arvot.nimi.trim()
      if (nimi) {
        pyynto.nimi = nimi
      }
      const vastaus = await this._firebaseLemonaid.functionsCall<WebMaksukorttiLisaaPyynto, WebMaksukorttiLisaaVastaus>('maksukorttiLisaaWeb', pyynto, undefined, 'europe-north1')
      if (vastaus.e) {
        console.error(`Maksukortin lisäys epäonnistui: ${vastaus.e}`)
        this.virhe.set(this._translationService.lokalisoi(`tositteet.maksukortit.lisaa-dialog.virheet.${vastaus.e}`))
        return
      }
      this._dialogRef.close(true)
    } catch (err) {
      this._errorHandler.handleError(err)
      this.virhe.set(this._translationService.lokalisoi('tositteet.maksukortit.lisaa-dialog.virheet.unknown-error'))
    } finally {
      this.tallennetaan.set(false)
    }
  }
}
